/**
 * 260812: "이 번호는 이미 ○○로 가입돼 있음" 차단 안내 — 소셜 로그인 첫 왕복(409)에서 받은 정보를 들고 있다가
 * 안내 모달을 띄우는 한 곳.
 *
 * 왜 모듈로 뺐나 — 소셜 로그인 실패 후 '가입 화면으로 보내는' 분기가 세 군데다
 * (signIn/index.vue = 웹 카카오·구글·애플, NaverCallback.vue = 웹 네이버 리다이렉트,
 * NativeCall.js = 앱 네이티브 콜백). 안내 문구·버튼을 각자 박으면 다음에 한 곳만 고쳐지는
 * '같은 의미 분산'이 그대로 재현된다. 판정은 BE 한 곳, 렌더도 여기 한 곳.
 *
 * store(Vuex)가 아니라 모듈 변수인 이유: 로그인 왕복 ~ 모달 사이에만 사는 순수 UI 인계값이라
 * 영속/리셋 대상이 아니다. SPA 라우팅으로는 살아남고 새로고침이면 사라지는 게 맞는 수명이다.
 */
import { JOIN_TYPE } from '@/common/constants/ConstCode';
import { showDoneModal, showTextModal } from '@/common/utils/ModalState';
import { isApp } from '@/common/utils/module/IsMobileSupport';
import { webNaverLogin } from '@/common/utils/module/WebSocialLogin';

/** BE(ControllerAdvisorConfig)가 409로 내려주는 식별자. 문구 파싱으로 분기하지 않기 위한 계약. */
export const SOCIAL_PHONE_ALREADY_JOINED = 'SOCIAL_PHONE_ALREADY_JOINED';

let pending = null;

/** 로그인 응답이 409였을 때 store action이 넣어둔다. 그 외 응답에서는 null로 지워 stale 안내를 막는다. */
export const setSocialJoinBlock = block => {
  pending = block && block.code === SOCIAL_PHONE_ALREADY_JOINED ? block : null;
};

export const hasSocialJoinBlock = () => pending !== null;

/** 한 번만 쓴다 — 읽으면서 비운다(뒤로가기·재진입 때 옛 안내가 다시 뜨지 않게). */
export const consumeSocialJoinBlock = () => {
  const block = pending;
  pending = null;
  return block;
};

/**
 * 그 provider 로그인을 지금 바로 띄울 수 있는가 = '누르면 실제로 뭔가 일어나는가'.
 * 막았는데 나가는 문까지 죽어 있으면 원래 증상보다 나쁘다.
 *
 * - GENERAL(이메일): 띄울 소셜이 없다. 로그인 화면의 이메일 폼이 곧 출구다.
 * - 웹 애플: WebSocialLogin이 'APPLE'을 지원하지 않아 throw로 끝난다(catch가 console.error만) —
 *   iOS/Safari라 버튼이 보이더라도 눌러야 아무 일도 안 일어나는 죽은 버튼이 된다. 앱만 허용.
 * - 웹 구글: webGoogleLogin이 살아 있어 허용.
 */
const canRelaunch = (joinType, sameProvider) => {
  if (!joinType || joinType === JOIN_TYPE.GENERAL.value) return false;
  // 같은 provider의 다른 계정이면 재실행이 곧 같은 차단의 반복이다(빠져나갈 수 없는 고리).
  if (sameProvider) return false;
  if (isApp()) return true;
  if (joinType === JOIN_TYPE.APPLE.value) return false;
  return true;
};

/**
 * 차단 안내 모달.
 * @param block     {code, message, joinType} — message는 BE가 만든 문구를 그대로 쓴다(가입 마지막 단계에서
 *                  쓰던 문구와 같은 것. 두 벌로 갈라두면 한쪽만 고쳐진다).
 * @param onRelaunch 그 소셜 로그인을 시작하는 함수(joinType 인자). 웹=signIn의 startSnsLogin, 앱=nativeSnsLogin.
 *                   ⚠️ 사용자 클릭 체인 안에서 동기 호출돼야 한다 — setTimeout으로 감싸면 카카오 팝업이 차단된다.
 */
export const showSocialJoinBlockedModal = (block, onRelaunch) => {
  if (!block) return;
  const joinType = block.joinType;
  const label = JOIN_TYPE[joinType]?.label || '';
  const contents = (block.message || '이미 가입된 휴대폰번호입니다.').replace(/\n/g, '<br>');

  // 260818: 같은 provider의 다른 계정 = 재실행 버튼이 없는 막다른 지점이다. 여기서 출구를 안 알려주면
  //   사용자는 로그인 화면의 그 소셜 버튼을 계속 누르게 되고, provider 세션이 이미 붙어 있어 같은 계정이
  //   자동 승인돼 돌아온다(테섭 260818 실측: 네이버 4초 간격 연속 차단). 체감은 무한루프다.
  //   ⚠️ 앱은 이미 NaverIdLoginSDK.logout() 후 인증한다 — 그건 우리 토큰만 지울 뿐이고 계정은
  //     폰의 네이버 앱 로그인 상태가 정한다. 우리 코드로 강제할 수단이 없어 '안내'가 유일한 출구다.
  // 260818: 막다른 지점의 '진짜 출구'. 안내 문구만 두면 사용자는 같은 버튼을 계속 누르고, provider
  //   세션이 붙어 있어 같은 계정이 자동 승인돼 돌아온다. 계정 선택을 강제할 수 있는 provider는
  //   여기서 그 경로를 버튼으로 준다.
  //   네이버 = 웹 authorize 의 auth_type=reauthenticate. 앱에서도 동작한다(WebView 제자리 로드 +
  //   콜백이 같은 오리진). 네이티브 SDK 로는 불가능해서 앱/웹 모두 이 웹 경로로 내보낸다.
  if (block.sameProvider && joinType === JOIN_TYPE.NAVER.value) {
    const msg = `${contents}<br><br>지금 기기에 <b>다른 ${label} 계정</b>이 로그인돼 있어요.<br>아래 버튼을 누르면 ${label} 로그인 화면이 다시 떠서 <b>가입할 때 쓰신 계정</b>으로 바꿀 수 있어요.`;
    showDoneModal(msg, `다른 ${label} 계정으로 로그인`, '닫기', () => webNaverLogin(true), null, null, '가입 안내');
    return;
  }

  // 계정 강제 전환 경로가 없는 provider(카카오·구글·애플) — 안내만. 눌러도 같은 계정이 돌아오는
  //   버튼을 다는 건 원래 증상보다 나쁘다(canRelaunch 주석과 같은 이유).
  const deadEnd = block.sameProvider && label
    ? `${contents}<br><br>지금은 <b>다른 ${label} 계정</b>으로 로그인돼 있어요.<br>${label} 앱이나 브라우저에서 계정을 바꾸거나 로그아웃한 뒤 다시 시도해 주세요.`
    : contents;

  if (!label || !canRelaunch(joinType, block.sameProvider) || typeof onRelaunch !== 'function') {
    showTextModal(deadEnd, '확인');
    return;
  }

  showDoneModal(contents, `${label}로 로그인`, '닫기', () => onRelaunch(joinType), null, null, '가입 안내');
};
